import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { Navigate, useNavigate, useSearchParams } from 'react-router';
import { loginForm, type LoginForm } from '@repo-manager/shared';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  toast,
} from '@repo-manager/ui';

import { useSession, useSupabase } from '../lib/supabase-context';

export function LoginPage() {
  const { status } = useSession();
  const supabase = useSupabase();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const form = useForm<LoginForm>({
    resolver: zodResolver(loginForm),
    defaultValues: { email: '', password: '' },
  });

  if (status === 'authed') {
    const next = params.get('next') ?? '/';
    return <Navigate to={next} replace />;
  }

  async function onSubmit(values: LoginForm) {
    const { error } = await supabase.auth.signInWithPassword(values);
    if (error) {
      toast.error('Sign-in failed', { description: error.message });
      return;
    }
    const next = params.get('next') ?? '/';
    navigate(next, { replace: true });
  }

  return (
    <div className="mx-auto flex max-w-md flex-col gap-6 py-12">
      <Card>
        <CardHeader>
          <CardTitle>Sign in</CardTitle>
          <CardDescription>Email + password authentication via Supabase.</CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form className="space-y-4" onSubmit={form.handleSubmit(onSubmit)}>
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input type="email" autoComplete="email" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Password</FormLabel>
                    <FormControl>
                      <Input type="password" autoComplete="current-password" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Button type="submit" className="w-full" disabled={form.formState.isSubmitting}>
                Sign in
              </Button>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
}
